import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { withLabel } from '@/se/components/Label';
import MonitoringAssignment from '../../../entities/hospitals/MonitoringAssignment';
import GroupAdminAssignment from '../../../entities/hospitals/GroupAdminAssignment';
import { LinkInput } from '../HospitalInput';
import { HospitalFormValue } from '../types';

const LabeledMonitors = withLabel('Monitoring Engineers')(MonitoringAssignment) as any;
const LabeledGroupAdmins = withLabel('Group Admins')(GroupAdminAssignment) as any;

const LabeledGoogleReview = withLabel('Google')(LinkInput) as any;
const LabeledYelpReview = withLabel('Yelp!')(LinkInput) as any;
const LabeledExternalLink = withLabel('External Link')(LinkInput) as any;

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

interface PeopleIntegrationsTabProps {
  value: HospitalFormValue;
  onChange: (value: HospitalFormValue) => void;
  disabled?: boolean;
}

const PeopleIntegrationsTab: React.FC<PeopleIntegrationsTabProps> = ({ value, onChange, disabled }) => {
  const classes = useStyles();

  const handleField = (field: keyof HospitalFormValue) => (val: any) => {
    onChange({ ...value, [field]: val });
  };

  const handleNestedField = (field: keyof HospitalFormValue, subField: string) => (val: any) => {
    onChange({
      ...value,
      [field]: {
        ...(value[field] as any),
        [subField]: val,
      },
    });
  };

  return (
    <Box maxWidth={700}>
      <Box className={classes.section}>
        <LabeledMonitors value={value.monitors || []} onChange={handleField('monitors')} disabled={disabled} />
      </Box>

      <Box className={classes.section}>
        <LabeledGroupAdmins value={value.groupAdmins || []} onChange={handleField('groupAdmins')} disabled={disabled} />
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Patient Review Integrations</Typography>
        <LabeledGoogleReview
          value={value.reviewIntegrations?.google || { link: '', enabled: false }}
          onChange={handleNestedField('reviewIntegrations', 'google')}
          disabled={disabled}
        />
        <LabeledYelpReview
          value={value.reviewIntegrations?.yelp || { link: '', enabled: false }}
          onChange={handleNestedField('reviewIntegrations', 'yelp')}
          disabled={disabled}
        />
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Caretaker Review Integrations</Typography>
        <LabeledGoogleReview
          value={value.caretakerReviewIntegrations?.google || { link: '', enabled: false }}
          onChange={handleNestedField('caretakerReviewIntegrations', 'google')}
          disabled={disabled}
        />
        <LabeledYelpReview
          value={value.caretakerReviewIntegrations?.yelp || { link: '', enabled: false }}
          onChange={handleNestedField('caretakerReviewIntegrations', 'yelp')}
          disabled={disabled}
        />
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Ospitek Orders Integrations</Typography>
        <LabeledExternalLink
          value={value.ospitekOrdersIntegrations?.externalLink || { link: '', enabled: false }}
          onChange={handleNestedField('ospitekOrdersIntegrations', 'externalLink')}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
};

export default PeopleIntegrationsTab;
